import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from '@mui/material';
import {
  Wallet as WalletIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { fetchWallets, deleteWallet } from '../walletApi';
import { useTranslation } from 'react-i18next';

const Wallets = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWallets();
  }, []);

  const loadWallets = async () => {
    setLoading(true);
    try {
      const data = await fetchWallets();
      setWallets(data || []);
    } catch (error) {
      console.error('Error loading wallets:', error);
      Swal.fire(t('Error'), t('WalletLoadFail') || 'Failed to load wallets', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: t('Are_You_Sure'),
      text: t('DeleteWarning'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('Yes_Delete_It'),
      cancelButtonText: t('Cancel')
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteWallet(id);
          Swal.fire(t('Deleted'), t('WalletDeletedSuccess') || 'Wallet deleted successfully', 'success');
          loadWallets();
        } catch (error) {
          Swal.fire(t('Error'), error.response?.data?.message || t('WalletDeleteFail'), 'error');
        }
      }
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardHeader
          title={t('ManageWallets') || 'Manage Wallets'}
          subheader={t('ManageWalletsDesc') || 'Add, edit, or remove payment wallets'}
          avatar={<WalletIcon color="primary" fontSize="large" />}
          action={
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/admin/wallets/add')}>
              {t('AddWallet') || 'Add Wallet'}
            </Button>
          }
          sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
        />
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('ID')}</TableCell>
                  <TableCell>{t('Image')}</TableCell>
                  <TableCell>{t('Name')}</TableCell>
                  <TableCell>{t('Description')}</TableCell>
                  <TableCell>{t('Status')}</TableCell>
                  <TableCell align="right">{t('Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {wallets.map((wallet) => (
                  <TableRow key={wallet.Id}>
                    <TableCell>{wallet.Id}</TableCell>
                    <TableCell>
                      {wallet.ImageUrl && (
                        <img 
                          src={wallet.ImageUrl} 
                          alt={wallet.Name} 
                          style={{ width: 60, height: 40, objectFit: 'contain', borderRadius: 4 }} 
                        />
                      )}
                    </TableCell>
                    <TableCell>{wallet.Name}</TableCell>
                    <TableCell>{wallet.Description || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={wallet.IsActive ? t('Active') : t('Inactive')} 
                        color={wallet.IsActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton color="primary" onClick={() => navigate(`/admin/wallets/edit/${wallet.Id}`)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(wallet.Id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {wallets.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">{t('NoWalletsFound') || 'No wallets found'}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Wallets;
